// /community-Page — Discord-Gateway-Invite (ersetzt den eigenen Chat-Clone).
//
// Statt einer selbstgebauten Chat-Oberfläche zeigt die Community-Seite jetzt
// eine einzelne, hochwertige Einladungs-Landing, die zum echten Discord-Server
// führt. Die alten Community-Komponenten/Hooks/Backend-Files bleiben dormant
// im Repo — sie werden hier nur nicht mehr importiert.

import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Bell, MessageSquare, Sparkles, TrendingUp } from 'lucide-react';
import { useEvePageContextStore } from '@/stores/eve-page-context-store';

/** Einladungs-Link zum UPRO-Capital-Discord-Server. */
const DISCORD_INVITE_URL = 'https://discord.gg/Vjg5sxgrk';

/**
 * Offizielle Discord-Wortmarke (Clyde-Logo) als Inline-SVG — lucide hat kein
 * Discord-Icon. `currentColor` erlaubt blurple/weiß je nach Kontext.
 */
function DiscordLogo({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 127.14 96.36"
      fill="currentColor"
      aria-hidden="true"
      className={className}
    >
      <path d="M107.7 8.07A105.15 105.15 0 0 0 81.47 0a72.06 72.06 0 0 0-3.36 6.83 97.68 97.68 0 0 0-29.11 0A72.37 72.37 0 0 0 45.64 0a105.89 105.89 0 0 0-26.25 8.09C2.79 32.65-1.71 56.6.54 80.21a105.73 105.73 0 0 0 32.17 16.15 77.7 77.7 0 0 0 6.89-11.11 68.42 68.42 0 0 1-10.85-5.18c.91-.66 1.8-1.34 2.66-2a75.57 75.57 0 0 0 64.32 0c.87.71 1.76 1.39 2.66 2a68.68 68.68 0 0 1-10.87 5.19 77 77 0 0 0 6.89 11.1 105.25 105.25 0 0 0 32.19-16.14c2.64-27.38-4.51-51.11-18.9-72.15ZM42.45 65.69C36.18 65.69 31 60 31 53s5-12.74 11.43-12.74S54 46 53.89 53s-5.05 12.69-11.44 12.69Zm42.24 0C78.41 65.69 73.25 60 73.25 53s5-12.74 11.44-12.74S96.23 46 96.12 53s-5.04 12.69-11.43 12.69Z" />
    </svg>
  );
}

interface ValueItem {
  icon: typeof Sparkles;
  label: string;
}

/** Vier Mehrwert-Punkte für die Community — kurze, klare deutsche Labels. */
const VALUE_ITEMS: ReadonlyArray<ValueItem> = [
  { icon: Sparkles, label: 'Tägliche EVE-Briefings — Morgen, Markt & Wochenende' },
  { icon: TrendingUp, label: 'Setups & Trade-Ideen teilen' },
  { icon: MessageSquare, label: 'Fragen an die Community & an Doc' },
  { icon: Bell, label: 'News- & Whale-Alerts in Echtzeit' },
];

const containerVariants = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.07, delayChildren: 0.05 },
  },
} as const;

const itemVariants = {
  hidden: { opacity: 0, y: 14 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: 'spring', stiffness: 240, damping: 26 },
  },
} as const;

export function CommunityPage() {
  const setPageContext = useEvePageContextStore((s) => s.setPageContext);
  const clearPageContext = useEvePageContextStore((s) => s.clearPageContext);

  // EVE-Page-Context setzen, damit die EVE-Bubble den Tab kennt.
  useEffect(() => {
    setPageContext({
      pageKey: '/community',
      title: 'Community',
      summary:
        'Die UPRO-Capital-Community läuft auf Discord. Live-Austausch mit Tradern, ' +
        'tägliche EVE-Briefings, Setups und News- & Whale-Alerts. ' +
        'Beitritt über die Discord-Einladung auf dieser Seite.',
    });
    return () => clearPageContext();
  }, [setPageContext, clearPageContext]);

  return (
    <div className="relative h-full overflow-y-auto">
      {/* ── Atmosphärischer Hintergrund: amber- + blurple-Glow über Dark-Base ── */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        {/* Amber-Glow oben rechts */}
        <div className="absolute -top-32 right-[-10%] h-[480px] w-[480px] rounded-full bg-amber-500/12 blur-[140px]" />
        {/* Discord-Blurple-Glow unten links */}
        <div className="absolute bottom-[-15%] left-[-10%] h-[520px] w-[520px] rounded-full bg-[#5865F2]/14 blur-[150px]" />
        {/* Feines Grid für Tiefe statt flachem Schwarz */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              'linear-gradient(to right, rgb(148 163 184) 1px, transparent 1px), linear-gradient(to bottom, rgb(148 163 184) 1px, transparent 1px)',
            backgroundSize: '46px 46px',
            maskImage: 'radial-gradient(ellipse 70% 60% at 50% 40%, black 30%, transparent 100%)',
          }}
        />
      </div>

      {/* ── Zentrierte Komposition mit großzügigem Whitespace ── */}
      <div className="relative flex min-h-full items-center justify-center px-6 py-16 sm:py-20">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="show"
          className="w-full max-w-2xl text-center"
        >
          {/* ── Discord-Mark als Hero-Emblem ── */}
          <motion.div variants={itemVariants} className="mb-8 flex justify-center">
            <div className="relative grid size-20 place-items-center rounded-2xl border border-[#5865F2]/30 bg-[#5865F2]/10 shadow-[0_0_60px_-12px_rgba(88,101,242,0.55)]">
              <DiscordLogo className="size-10 text-[#7d88f5]" />
            </div>
          </motion.div>

          {/* ── Hero-Heading + Subtitle ── */}
          <motion.h1
            variants={itemVariants}
            className="text-4xl font-bold tracking-tight text-slate-50 sm:text-5xl"
          >
            UPRO Capital Community
          </motion.h1>
          <motion.p
            variants={itemVariants}
            className="mx-auto mt-4 max-w-xl text-[15px] leading-relaxed text-slate-400 sm:text-base"
          >
            Live-Austausch mit Tradern — EVE-Briefings, Setups &amp; Marktupdates,
            direkt im Discord.
          </motion.p>

          {/* ── Mehrwert-Liste (2×2-Grid) ── */}
          <motion.ul
            variants={itemVariants}
            className="mx-auto mt-10 grid max-w-xl gap-3 text-left sm:grid-cols-2"
          >
            {VALUE_ITEMS.map(({ icon: Icon, label }) => (
              <li
                key={label}
                className="glass flex items-start gap-3 rounded-2xl border border-slate-700/40 px-4 py-3.5 transition-colors hover:border-slate-600/60"
              >
                <span className="mt-0.5 grid size-8 shrink-0 place-items-center rounded-lg border border-amber-500/25 bg-amber-500/10">
                  <Icon className="size-4 text-amber-400" />
                </span>
                <span className="text-[13px] leading-snug text-slate-300">{label}</span>
              </li>
            ))}
          </motion.ul>

          {/* ── Primärer CTA: Discord-Blurple, Logo, Glow-on-Hover ── */}
          <motion.div variants={itemVariants} className="mt-12">
            <a
              href={DISCORD_INVITE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex w-full items-center justify-center gap-3 rounded-2xl bg-[#5865F2] px-8 py-4 text-base font-semibold text-white shadow-lg shadow-[#5865F2]/25 transition-all duration-200 hover:bg-[#4752c4] hover:shadow-xl hover:shadow-[#5865F2]/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5865F2] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0a0f] sm:w-auto"
            >
              <DiscordLogo className="size-6 transition-transform duration-200 group-hover:scale-110" />
              Discord beitreten
            </a>

            {/* ── Sekundäre Zeile unter dem CTA ── */}
            <p className="mt-4 text-[12.5px] text-slate-500">
              EVE &amp; Satoshi posten täglich live im Server.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
